/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { mcpApprovalCreateRequest } from "../funcs/mcpApprovalCreateRequest.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { ApprovalRequestSummary } from "../models/components/approvalrequestsummary.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  CreateMcpApprovalRequestRequest,
  CreateMcpApprovalRequestSecurity,
} from "../models/operations/createmcpapprovalrequest.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type CreateMcpApprovalRequestMutationVariables = {
  request: CreateMcpApprovalRequestRequest;
  security?: CreateMcpApprovalRequestSecurity | undefined;
  options?: RequestOptions;
};

export type CreateMcpApprovalRequestMutationData = ApprovalRequestSummary;

export type CreateMcpApprovalRequestMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * createRequest mcpApproval
 *
 * @remarks
 * Ask for an MCP server to be reviewed. Repeat asks for the same server attach to the existing review rather than opening a second one.
 */
export function useCreateMcpApprovalRequestMutation(
  options?: MutationHookOptions<
    CreateMcpApprovalRequestMutationData,
    CreateMcpApprovalRequestMutationError,
    CreateMcpApprovalRequestMutationVariables
  >,
): UseMutationResult<
  CreateMcpApprovalRequestMutationData,
  CreateMcpApprovalRequestMutationError,
  CreateMcpApprovalRequestMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildCreateMcpApprovalRequestMutation(client, options),
    ...options,
  });
}

export function mutationKeyCreateMcpApprovalRequest(): MutationKey {
  return ["@gram/client", "mcpApproval", "createRequest"];
}

export function buildCreateMcpApprovalRequestMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: CreateMcpApprovalRequestMutationVariables,
  ) => Promise<CreateMcpApprovalRequestMutationData>;
} {
  return {
    mutationKey: mutationKeyCreateMcpApprovalRequest(),
    mutationFn: function createMcpApprovalRequestMutationFn({
      request,
      security,
      options,
    }): Promise<CreateMcpApprovalRequestMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(mcpApprovalCreateRequest(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
